import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { Typography, FormControlLabel, Checkbox, Button, Divider, CircularProgress, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import CompetitionDropdown from '../components/forms/Dropdowns/CompetitionDropdown';
import { CommonDataContext } from '../firebase/CommonDataContext';
import { UserDataContext } from '../firebase/UserDataContext';
import { FirebaseNewContext } from '../firebase/FirebaseNewContext';
import { GlobalSettingsContext } from '../firebase/GlobalSettingsContext';

import app from 'firebase/compat/app';
import { initializeApp, getApp } from '@firebase/app';
import { getAuth, signOut, createUserWithEmailAndPassword } from '@firebase/auth';

const newFirebaseUserApp = () => {
  if (app.apps.find(entry => entry.name === 'SERVICE_ACCOUNT')) return getApp('SERVICE_ACCOUNT');
  const config = {
    apiKey: process.env.REACT_APP_API_KEY,
    authDomain: process.env.REACT_APP_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_DATABASE_URL,
    projectId: process.env.REACT_APP_PROJECT_ID,
    storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  };
  return initializeApp(config, 'SERVICE_ACCOUNT');
};
const secondaryApp = newFirebaseUserApp();
const secondaryAuth = getAuth(secondaryApp);

// Admin Export page with UX polish: counts, disable while exporting, sanitized cells, autofit columns
const AdminExportPage = () => {
  const { teamsMap, eventsMap, scoresheetsMap, usersMap, judgesMap, teamUsersMap, liabilityMap, adminUsersMap } = useContext(CommonDataContext);
  const { defaultSelectedCompetition } = useContext(UserDataContext);
  const { firebase } = useContext(FirebaseNewContext);
  const { globalSettings } = useContext(GlobalSettingsContext);

  const [competition, setCompetition] = useState(defaultSelectedCompetition || '');

  // Teams
  const [teamsAll, setTeamsAll] = useState(false);
  const [teamsScores, setTeamsScores] = useState(false);
  const [teamMembers, setTeamMembers] = useState(false);
  const [teamsGroupAll, setTeamsGroupAll] = useState(false);

  // Events
  const [eventsAll, setEventsAll] = useState(false);
  const [eventsPenalties, setEventsPenalties] = useState(false);
  const [eventsBase, setEventsBase] = useState(false);
  const [assocTeams, setAssocTeams] = useState(false);
  const [judges, setJudges] = useState(false);
  const [eventsGroupAll, setEventsGroupAll] = useState(false);

  // Users
  const [users, setUsers] = useState(false);
  const [adminUsers, setAdminUsers] = useState(false);
  const [liabilities, setLiabilities] = useState(false);
  const [usersGroupAll, setUsersGroupAll] = useState(false);

  // Categories & Divisions
  const [categories, setCategories] = useState(false);
  const [divisions, setDivisions] = useState(false);
  const [categoriesCount, setCategoriesCount] = useState(0);
  const [divisionsCount, setDivisionsCount] = useState(0);

  const [includeMeta, setIncludeMeta] = useState(false);

  const [exporting, setExporting] = useState(false);

  useEffect(() => setCompetition(defaultSelectedCompetition || ''), [defaultSelectedCompetition]);

  // group toggle behaviors
  useEffect(() => { setTeamsAll(teamsGroupAll); setTeamsScores(teamsGroupAll); setTeamMembers(teamsGroupAll); }, [teamsGroupAll]);
  useEffect(() => { setEventsAll(eventsGroupAll); setEventsPenalties(eventsGroupAll); setEventsBase(eventsGroupAll); setAssocTeams(eventsGroupAll); setJudges(eventsGroupAll); }, [eventsGroupAll]);
  useEffect(() => { setUsers(usersGroupAll); setAdminUsers(usersGroupAll); setLiabilities(usersGroupAll); }, [usersGroupAll]);

  // fetch counts for categories/divisions
  useEffect(() => {
    let mounted = true;
    const fetchCounts = async () => {
      try {
        if (!competition) { setCategoriesCount(0); setDivisionsCount(0); return; }
        if (typeof firebase.getCategoryMap === 'function') {
          const allCats = await firebase.getCategoryMap();
          const forComp = (allCats && allCats[competition]) || {};
          if (mounted) setCategoriesCount(Object.keys(forComp).length);
        }
        if (typeof firebase.getDivisionMapByCompetition === 'function') {
          const divs = await firebase.getDivisionMapByCompetition(competition);
          if (mounted) setDivisionsCount(Object.keys(divs || {}).length);
        }
      } catch (e) {
        console.error('Failed to fetch category/division counts', e);
      }
    };
    fetchCounts();
    return () => { mounted = false; };
  }, [competition, firebase]);

  // derived counts
  const counts = useMemo(() => {
    const tMap = (teamsMap && teamsMap[competition]) || {};
    const eMap = (eventsMap && eventsMap[competition]) || {};
    const ssComp = (scoresheetsMap && scoresheetsMap[competition]) || {};
    const eventSheets = ssComp.events || {};
    const teamsCount = Object.keys(tMap).length;
    const eventsCount = Object.keys(eMap).length;
    let assocCount = 0;
    Object.keys(eMap).forEach(k => { const ev = eMap[k] || {}; const a = ev.associatedTeams || []; assocCount += Array.isArray(a) ? a.length : Object.keys(a || {}).length; });
    let penaltiesCount = 0; Object.keys(eventSheets).forEach(k => { const s = eventSheets[k] || {}; const p = s.penalties || []; penaltiesCount += Array.isArray(p) ? p.length : 0; });
    let teamMembersCount = 0; Object.keys(tMap).forEach(k => { const t = tMap[k] || {}; const m = t.teamMembers || []; teamMembersCount += Array.isArray(m) ? m.length : Object.keys(m || {}).length; });
    const judgesCount = Object.keys(judgesMap || {}).length;
    const usersCount = Object.keys(usersMap || {}).length;
    const adminCount = Object.keys(adminUsersMap || {}).length;
    const liabilityCount = Object.keys(((liabilityMap || {})[competition] || {}).signatories || {}).length;
    return { teamsCount, eventsCount, assocCount, penaltiesCount, teamMembersCount, judgesCount, usersCount, adminCount, liabilityCount };
  }, [competition, teamsMap, eventsMap, scoresheetsMap, usersMap, judgesMap, adminUsersMap, liabilityMap]);

  // Load SheetJS (try local vendor first, fallback CDN)
  const loadSheetJS = async () => {
    if (window && window.XLSX) return window.XLSX;

    const loadScript = (src, timeout = 10000) => new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.async = true;
      let timer = setTimeout(() => {
        s.onerror = null; s.onload = null;
        reject(new Error('script load timeout'));
      }, timeout);
      s.onload = () => { clearTimeout(timer); resolve(); };
      s.onerror = () => { clearTimeout(timer); reject(new Error('script load error')); };
      document.head.appendChild(s);
    });

    const tryLocal = async () => {
      const local = '/xlsx.full.min.js';
      try {
        const resp = await fetch(local, { cache: 'no-store' });
        if (!resp.ok) throw new Error('local file not found');
        const text = await resp.text();
        // if the fetched text begins with '<' it's likely HTML (index.html/404)
        if (text.trim().startsWith('<')) throw new Error('local xlsx appears to be HTML');
        const blob = new Blob([text], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);
        try {
          await loadScript(url);
        } finally {
          URL.revokeObjectURL(url);
        }
        if (window && window.XLSX) return window.XLSX;
        throw new Error('local xlsx loaded but XLSX not found');
      } catch (err) {
        console.warn('local SheetJS load failed:', err.message || err);
        return null;
      }
    };

    const localResult = await tryLocal();
    if (localResult) return localResult;

    // fallback to CDN
    const cdn = 'https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js';
    await loadScript(cdn);
    if (window && window.XLSX) return window.XLSX;
    throw new Error('Failed to load SheetJS');
  };

  // helpers
  const sanitize = (v) => { if (v === null || v === undefined) return ''; const s = String(v); return (/^[=+\-@]/.test(s) || s.startsWith('\t') || s.startsWith('\r')) ? ("'" + s) : s; };
  const autofit = (ws, aoa) => { const cols = (aoa[0] || []).length; const widths = new Array(cols).fill(8); for (let r=0;r<aoa.length;r++){ for (let c=0;c<cols;c++){ const val = aoa[r][c]==null?'':String(aoa[r][c]); widths[c] = Math.max(widths[c], Math.min(60, val.length+1)); } } ws['!cols'] = widths.map(wch => ({ wch })); };
  const finalize = (XLSX, ws, aoa) => { autofit(ws, aoa); const last = aoa[0] ? aoa[0].length-1 : 0; const colLetter = (n) => { let s=''; n++; while(n>0){ const m=(n-1)%26; s=String.fromCharCode(65+m)+s; n=Math.floor((n-m)/26);} return s; }; const lastCol = colLetter(last); try{ XLSX.utils.sheet_set_auto_filter(ws, `A1:${lastCol}1`); }catch(e){} };

  const appendSheet = (XLSX, wb, name, aoa) => { const safe = aoa.map(r => r.map(sanitize)); const ws = XLSX.utils.aoa_to_sheet(safe); finalize(XLSX, ws, safe); XLSX.utils.book_append_sheet(wb, ws, name); };

  const generateXlsx = async () => {
    if (!competition) { alert('Please select a competition'); return; }
    setExporting(true);
    let XLSX;
    try { XLSX = await loadSheetJS(); } catch (e) { console.error('XLSX load failed', e); alert('Could not load spreadsheet library'); setExporting(false); return; }
    const wb = XLSX.utils.book_new();

    // refresh data if contexts empty
    let teamsData = (teamsMap && teamsMap[competition]) || {};
    if ((!teamsData || Object.keys(teamsData).length===0) && typeof firebase.getTeamsMap==='function') { try{ const all = await firebase.getTeamsMap(); teamsData=(all&&all[competition])||{}; }catch(e){console.error(e);} }
    let eventsData = (eventsMap && eventsMap[competition]) || {};
    if ((!eventsData || Object.keys(eventsData).length===0) && typeof firebase.getEventsByCompetition==='function') { try{ eventsData = await firebase.getEventsByCompetition(competition) || {}; }catch(e){console.error(e);} }
    let scoresheetsData = (scoresheetsMap && scoresheetsMap[competition]) || {};

    // Load category/division maps for name resolution (prefer globalSettings if available)
    let categoryMapForComp = {};
    let divisionMapForComp = {};
    try {
      if (globalSettings && globalSettings.categories) {
        categoryMapForComp = globalSettings.categories[competition] || {};
      } else if (typeof firebase.getCategoryMap === 'function') {
        const allCats = await firebase.getCategoryMap() || {};
        categoryMapForComp = (allCats && allCats[competition]) || {};
      }
    } catch (e) { console.warn('category map fetch failed', e); }
    try {
      if (globalSettings && globalSettings.divisions) {
        divisionMapForComp = globalSettings.divisions[competition] || {};
      } else if (typeof firebase.getDivisionMapByCompetition === 'function') {
        divisionMapForComp = await firebase.getDivisionMapByCompetition(competition) || {};
      }
    } catch (e) { console.warn('division map fetch failed', e); }

    const exportNotes = [];

    const resolveCategoryName = (val) => {
      if (!val) return '';
      // if val is a key in map
      if (categoryMapForComp && (val in categoryMapForComp)) {
        const entry = categoryMapForComp[val];
        if (typeof entry === 'string') return entry;
        return entry && entry.name ? entry.name : '';
      }
      // if val matches a name in the values
      const found = Object.values(categoryMapForComp).find(e => (typeof e === 'string' ? e === val : e && e.name === val));
      if (found) return typeof found === 'string' ? found : found.name;
      return val;
    };

    const resolveDivisionName = (val) => {
      if (!val) return '';
      if (divisionMapForComp && (val in divisionMapForComp)) {
        const entry = divisionMapForComp[val];
        if (typeof entry === 'string') return entry;
        return entry && entry.name ? entry.name : '';
      }
      const found = Object.values(divisionMapForComp).find(e => (typeof e === 'string' ? e === val : e && e.name === val));
      if (found) return typeof found === 'string' ? found : found.name;
      return val;
    };

    // Categories (per-competition)
    if (categories) {
      const headers = ['categoryName','description'];
      const rows = Object.keys(categoryMapForComp).map(k => {
        const c = categoryMapForComp[k];
        if (typeof c === 'string') return [c, ''];
        if (!c) return ['',''];
        return [c.name || '', c.description || ''];
      });
      if (rows.length===0) rows.push(['(no categories found)']);
      appendSheet(XLSX, wb, 'Categories', [headers, ...rows]);
    }

    // Divisions (per-competition)
    if (divisions) {
      const headers = ['divisionName','description'];
      const rows = Object.keys(divisionMapForComp).map(k => {
        const d = divisionMapForComp[k];
        if (typeof d === 'string') return [d, ''];
        if (!d) return ['',''];
        return [d.name || '', d.description || ''];
      });
      if (rows.length===0) rows.push(['(no divisions found)']);
      appendSheet(XLSX, wb, 'Divisions', [headers, ...rows]);
    }

    // Teams (combined sheet)
    if (teamsAll) {
      const headers = ['teamNumber','teamName','categoryName','divisionName','teamMembers'];
      const rows = Object.keys(teamsData).map(k => {
        const t = teamsData[k]||{};
        // resolve category and division names using helpers
        const categoryName = resolveCategoryName(t.category);
        if (t.category && categoryName === t.category && t.category.length>0 && !(t.category in categoryMapForComp) && !Object.values(categoryMapForComp).some(c => (typeof c === 'string' ? c === t.category : (c && c.name === t.category)))) {
          exportNotes.push(`Team ${t.teamNumber||k}: category value '${t.category}' exported as-is (couldn't resolve to name)`);
        }
        const divisionName = resolveDivisionName(t.division);
        if (t.division && divisionName === t.division && t.division.length>0 && !(t.division in divisionMapForComp) && !Object.values(divisionMapForComp).some(d => (typeof d === 'string' ? d === t.division : (d && d.name === t.division)))) {
          exportNotes.push(`Team ${t.teamNumber||k}: division value '${t.division}' exported as-is (couldn't resolve to name)`);
        }
        // collect team member emails
        const members = t.teamMembers || {};
        const keys = Array.isArray(members) ? members : Object.keys(members||{});
        const emails = keys.map(mUid => { const member = (teamUsersMap && teamUsersMap[mUid]) || (usersMap && usersMap[mUid]) || {}; const email = (member.email || mUid || '') ; return String(email).toLowerCase().trim(); }).filter(Boolean);
        const membersCsv = emails.join(', ');
        return [t.teamNumber||'', t.teamName||'', categoryName||'', divisionName||'', membersCsv];
      });
      if (rows.length===0) rows.push(['(no teams found)']);
      appendSheet(XLSX, wb, 'Teams', [headers, ...rows]);
    }

    // Team Scores
    if (teamsScores) {
      let scoresMapLocal = {};
      try { if (typeof firebase.getScores==='function') scoresMapLocal = await firebase.getScores(competition) || {}; } catch(e){ console.error(e); }
      const headers = ['teamNumber','teamName','eventNumber','eventName','judgeEmail','finalScore','penalties'];
      const rows = Object.keys(scoresMapLocal).map(k => { const s = scoresMapLocal[k]||{}; return [s.team?.teamNumber||'', s.team?.teamName||'', s.event?.number||'', s.event?.name||'', (s.judge?.email||'').toLowerCase().trim(), s.finalScore||'', JSON.stringify(s.penalties||{})]; });
      if (rows.length===0) rows.push(['(no scores found)']);
      appendSheet(XLSX, wb, 'Team Scores', [headers, ...rows]);
    }

    // Base scoresheet
    if (eventsBase) {
      const globalSheet = (scoresheetsData && scoresheetsData['global']) || {};
      const aoa = [['field','value']];
      Object.keys(globalSheet).forEach(k => { if (k==='penalties') return; const v=globalSheet[k]; aoa.push([k, typeof v==='object' ? JSON.stringify(v) : v]); });
      if (aoa.length===1) aoa.push(['(no base scoresheet found)']);
      appendSheet(XLSX, wb, 'Base Scoresheet', aoa);
      const basePenalties = globalSheet.penalties || [];
      if (Array.isArray(basePenalties) && basePenalties.length>0) { const pH=['penaltyId','penaltyName','penaltyDescription','penaltyValue','custom']; const pR = basePenalties.map(p=>[p.id||'', p.name||'', p.description||'', p.value||'', !!p.custom]); appendSheet(XLSX, wb, 'Base Penalties', [pH, ...pR]); }
    }

    // Events (combined with judges and associated teams)
    if (eventsAll) {
      const allEvents = eventsData || {};
      const headers = ['eventNumber','eventName','fullTitle','company','isQuiz','excludeEvent','judges','associatedTeams'];
      const items = Object.keys(allEvents).map(uid => {
        const ev = allEvents[uid]||{};
        const num = ev.number||ev['number']||'';
        // collect judges for this event
        const judgeRefs = ev.judgesNew || ev.judges || ev.judgesNew || [];
        const judgeEmails = [];
        if (Array.isArray(judgeRefs)) {
          judgeRefs.forEach(j => {
            if (!j) return;
            if (typeof j === 'string') {
              const user = (usersMap && usersMap[j]) || (judgesMap && judgesMap[j]);
              const email = (user && user.email) ? String(user.email).toLowerCase().trim() : j.toLowerCase().trim();
              judgeEmails.push(email);
            } else if (typeof j === 'object') {
              const uid = j.uid || j.user || j.userUid || '';
              const user = (usersMap && usersMap[uid]) || (judgesMap && judgesMap[uid]);
              const email = (user && user.email) ? String(user.email).toLowerCase().trim() : (j.email ? String(j.email).toLowerCase().trim() : '');
              if (email) judgeEmails.push(email);
            }
          });
        } else if (typeof judgeRefs === 'object') {
          // maybe object map
          Object.keys(judgeRefs).forEach(k => { const j = judgeRefs[k]; const user = (usersMap && usersMap[k]) || (judgesMap && judgesMap[k]) || {}; const email = (user.email||j.email||'').toLowerCase().trim(); if (email) judgeEmails.push(email); });
        }
        const judgesCsv = Array.from(new Set(judgeEmails)).join(', ');

        // collect associated teams (by teamNumber)
        const assoc = ev.associatedTeams || [];
        const teamNumbers = [];
        if (Array.isArray(assoc)) {
          assoc.forEach(a => {
            if (!a) return;
            if (typeof a === 'string') {
              const t = (teamsData && teamsData[a]) || {};
              if (t && t.teamNumber) teamNumbers.push(t.teamNumber);
              else teamNumbers.push(a);
            } else if (typeof a === 'object') {
              // could be { uid, teamNumber, teamName }
              if (a.teamNumber) teamNumbers.push(a.teamNumber);
              else if (a.uid) {
                const t = (teamsData && teamsData[a.uid]) || {};
                if (t && t.teamNumber) teamNumbers.push(t.teamNumber);
                else teamNumbers.push(a.uid);
              }
            }
          });
        } else if (typeof assoc === 'object') {
          Object.keys(assoc).forEach(k => { const a = assoc[k]; if (!a) return; if (typeof a === 'string') { const t=(teamsData&&teamsData[a])||{}; teamNumbers.push(t.teamNumber||a); } else if (a.teamNumber) teamNumbers.push(a.teamNumber); else if (a.uid) { const t=(teamsData&&teamsData[a.uid])||{}; teamNumbers.push(t.teamNumber||a.uid); } });
        }
        const assocCsv = Array.from(new Set(teamNumbers)).join(', ');

        return { n:Number(num||0), row:[num||'', ev.name||'', ev.fullTitle||'', ev.company||'', !!ev.isQuiz, !!ev.excludeEvent, judgesCsv, assocCsv] };
      });
      items.sort((a,b)=>a.n-b.n);
      const rows = items.map(x=>x.row);
      if (rows.length===0) rows.push(['(no events found)']);
      appendSheet(XLSX, wb, 'Events', [headers, ...rows]);
    }

    // Event Penalties
    if (eventsPenalties) {
      const eventSheets = (scoresheetsData && scoresheetsData['events']) || {};
      const headers = ['eventNumber','eventName','penaltyId','penaltyName','penaltyDescription','penaltyValue','custom'];
      const collected = [];
      Object.keys(eventsData||{}).forEach(evUid => { const ev = eventsData[evUid]||{}; const sheet = eventSheets[evUid]||{}; const pens = sheet.penalties||[]; pens.forEach(p=>collected.push({ n:Number(ev.number||ev['number']||0), row:[ev.number||ev['number']||'', ev.name||'', p.id||'', p.name||'', p.description||'', p.value||'', !!p.custom] })); });
      collected.sort((a,b)=>a.n-b.n); const rows = collected.map(x=>x.row); if (rows.length===0) rows.push(['(no penalties found)']); appendSheet(XLSX, wb, 'Event Penalties', [headers, ...rows]);
    }

    if (includeMeta && globalSettings) { const compName=(globalSettings.competitions&&globalSettings.competitions[competition])||''; const meta=[['competitionUid', competition], ['competitionName', compName], ['defaultCompetition', globalSettings.defaultCompetition||''], ['showFinalScoresToTeams', (globalSettings.showFinalScoresToTeams||{})[competition]||false], ['lockCompetition', (globalSettings.lockCompetition||{})[competition]||false]]; appendSheet(XLSX, wb, 'Competition Metadata', [['field','value'], ...meta]); }

    // Export notes if any fallbacks occurred
    if (exportNotes && exportNotes.length>0) {
      const notesAoA = [['note'], ...exportNotes.map(n=>[n])];
      appendSheet(XLSX, wb, 'Export Notes', notesAoA);
    }

    if (wb.SheetNames.length===0) { alert('Nothing selected to export'); setExporting(false); return; }
    try { XLSX.writeFile(wb, `export_${competition}.xlsx`); console.info('Exported', wb.SheetNames); } catch(e){ console.error('write failed', e); alert('Failed to write file'); }
    setExporting(false);
  };

  const prepareImportSelection = () => {
    // recommended selections for import-ready XLSX
    const sel_includeMeta = false;
    const sel_categories = true;
    const sel_divisions = true;
    const sel_teamsAll = true;
    const sel_teamsScores = false;
    const sel_teamMembers = false;
    const sel_eventsAll = true;
    const sel_eventsPenalties = false;
    const sel_eventsBase = false;
    const sel_assocTeams = false;
    const sel_judges = false;
    const sel_users = false;
    const sel_adminUsers = false;
    const sel_liabilities = false;

    setIncludeMeta(sel_includeMeta);
    setCategories(sel_categories);
    setDivisions(sel_divisions);
    setTeamsAll(sel_teamsAll);
    setTeamsScores(sel_teamsScores);
    setTeamMembers(sel_teamMembers);
    setTeamsGroupAll(sel_teamsAll && sel_teamsScores && sel_teamMembers);
    setEventsAll(sel_eventsAll);
    setEventsPenalties(sel_eventsPenalties);
    setEventsBase(sel_eventsBase);
    setAssocTeams(sel_assocTeams);
    setJudges(sel_judges);
    setEventsGroupAll(sel_eventsAll && sel_eventsPenalties && sel_eventsBase && sel_assocTeams && sel_judges);
    setUsers(sel_users);
    setAdminUsers(sel_adminUsers);
    setLiabilities(sel_liabilities);
    setUsersGroupAll(sel_users && sel_adminUsers && sel_liabilities);
  };

  // --- Import tab state and helpers ---
  const [tabIndex, setTabIndex] = useState(0);
  const [importFileName, setImportFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [staged, setStaged] = useState({}); // { categories:[], divisions:[], teams:[], events:[], penalties:[] }
  const [stagedSummary, setStagedSummary] = useState({});
  const [applyInProgress, setApplyInProgress] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [importErrors, setImportErrors] = useState([]);

  // UI for staged review
  const [entityView, setEntityView] = useState('teams'); // categories, divisions, teams, events, penalties
  const [statusFilter, setStatusFilter] = useState('all'); // all, create, update, no-op, error
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const openDetail = (item) => { setDetailData(item); setDetailOpen(true); };
  const closeDetail = () => { setDetailOpen(false); setDetailData(null); };

  const onTabChange = (e, v) => setTabIndex(v);

  const resetStaging = () => { setStaged({}); setStagedSummary({}); setImportErrors([]); };

  // Review helpers
  const filteredItems = useMemo(() => {
    try {
      const list = (staged && staged[entityView]) ? staged[entityView].map((it, idx) => ({ ...it, __idx: idx })) : [];
      if (statusFilter && statusFilter !== 'all') return list.filter(i => String(i.status||'').toLowerCase() === String(statusFilter||'').toLowerCase());
      return list;
    } catch (e) { return []; }
  }, [staged, entityView, statusFilter]);

  const updateStagedItem = (entity, idx, newItem) => {
    setStaged(prev => {
      const copy = { ...(prev || {}) };
      const arr = Array.isArray(copy[entity]) ? [...copy[entity]] : [];
      arr[idx] = newItem;
      copy[entity] = arr;
      return copy;
    });
  };

  const downloadErrors = async () => {
    try {
      const XLSX = await loadSheetJS();
      const wb = XLSX.utils.book_new();
      const rows = (importErrors || []).map(e => [String(e)]);
      const aoa = [['error'], ...rows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, ws, 'Errors');
      XLSX.writeFile(wb, `import_errors_${competition || 'unknown'}.xlsx`);
    } catch (e) {
      // fallback: download plain text
      const blob = new Blob([ (importErrors || []).join('\n') ], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'import_errors.txt'; a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleFileInput = async (file) => {
    if (!file) return;
    setImportFileName(file.name);
    setParsing(true);
    resetStaging();
    let XLSX;
    try { XLSX = await loadSheetJS(); } catch (e) { console.error(e); alert('Failed to load SheetJS'); setParsing(false); return; }
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const parsed = {};
      const requiredSheets = ['Categories','Divisions','Teams','Events','Event Penalties','Competition Metadata','Export Notes'];
      wb.SheetNames.forEach(name => {
        try {
          const ws = wb.Sheets[name];
          if (!ws) return;
          const aoa = XLSX.utils.sheet_to_json(ws, { header:1, defval: '' });
          if (!aoa || aoa.length===0) return;
          const headers = aoa[0].map(h => String(h||'').trim());
          const rows = aoa.slice(1).map((r,ri) => {
            const obj = {};
            for (let c=0;c<headers.length;c++) obj[headers[c]] = (r[c]===undefined? '': r[c]);
            return { __row: ri+2, __raw: r, ...obj };
          });
          parsed[name] = { headers, rows };
        } catch(err) { console.warn('sheet parse failed', name, err); }
      });

      // build staged model
      const usersByEmail = {};
      Object.keys(usersMap||{}).forEach(uid => { const u = usersMap[uid]||{}; if (u.email) usersByEmail[String(u.email).toLowerCase().trim()] = { uid, ...u }; });

      const categoryMapForComp = (globalSettings && globalSettings.categories && globalSettings.categories[competition]) || {};
      const divisionMapForComp = (globalSettings && globalSettings.divisions && globalSettings.divisions[competition]) || {};
      const existingTeams = (teamsMap && teamsMap[competition]) || {};
      const existingEvents = (eventsMap && eventsMap[competition]) || {};

      const stages = { users: [], categories: [], divisions: [], teams: [], events: [], penalties: [] };
      const errors = [];

      // Users
      const userRows = parsed['Users'] ? parsed['Users'].rows : [];
      const seenUserEmails = new Set();
      userRows.forEach(r => {
        const email = String((r.email||'')).toLowerCase().trim();
        if (!email) { stages.users.push({ status:'error', row:r.__row, message:'Missing email', raw:r }); errors.push(`Users row ${r.__row}: missing email`); return; }
        if (seenUserEmails.has(email)) { stages.users.push({ status:'error', row:r.__row, message:'Duplicate email in file', raw:r }); errors.push(`Users row ${r.__row}: duplicate email`); return; }
        seenUserEmails.add(email);
        const role = String((r.role||'TEAM')).trim().toUpperCase();
        const defaultComp = String((r.defaultCompetition||'')).trim() || competition;
        // Name: prefer explicit firstName/lastName columns, else split a full name column, else fall back to email
        let firstName = String((r.firstName||'')).trim();
        let lastName = String((r.lastName||'')).trim();
        if (!firstName && !lastName) {
          const fullName = String((r.name||'')).trim();
          if (fullName) {
            const parts = fullName.split(/\s+/);
            lastName = parts.pop();
            firstName = parts.join(' ');
          }
        }
        const username = String((r.username||'')).trim() || (firstName && lastName ? `${firstName}${lastName}`.replace(/\s+/g,'').toLowerCase() : email.split('@')[0]);
        const existing = usersByEmail[email];
        const action = existing ? 'no-op' : 'create';
        stages.users.push({ status: action, row: r.__row, email, firstName, lastName, username, role, defaultCompetition: defaultComp, matchKey: existing ? existing.uid : null });
      });

      // Merge staged users into usersByEmail so team member resolution works
      stages.users.forEach(u => { if (u.status === 'create') usersByEmail[u.email] = { email: u.email, username: u.username, firstName: u.firstName, lastName: u.lastName, role: u.role }; });

      // Categories
      const cats = parsed['Categories'] ? parsed['Categories'].rows : [];
      const seenCatNames = new Set();
      cats.forEach(r => {
        const name = String((r.categoryName||'')).trim();
        const desc = String((r.description||'')||'').trim();
        if (!name) { stages.categories.push({ status:'error', row:r.__row, message:'Missing categoryName', raw:r }); errors.push(`Categories row ${r.__row}: missing name`); return; }
        const matchKey = Object.keys(categoryMapForComp).find(k => { const v=categoryMapForComp[k]; const n = (typeof v==='string'?v:(v&&v.name? v.name:'')); return String(n||'').trim().toLowerCase()===name.toLowerCase(); });
        const action = matchKey ? 'update' : 'create';
        if (seenCatNames.has(name.toLowerCase())) { stages.categories.push({ status:'error', row:r.__row, message:'Duplicate category in file', raw:r }); errors.push(`Categories row ${r.__row}: duplicate name`); return; }
        seenCatNames.add(name.toLowerCase());
        stages.categories.push({ status: action, row:r.__row, name, description:desc, matchKey });
      });

      // Divisions
      const divs = parsed['Divisions'] ? parsed['Divisions'].rows : [];
      const seenDivNames = new Set();
      divs.forEach(r => {
        const name = String((r.divisionName||'')).trim();
        const desc = String((r.description||'')||'').trim();
        if (!name) { stages.divisions.push({ status:'error', row:r.__row, message:'Missing divisionName', raw:r }); errors.push(`Divisions row ${r.__row}: missing name`); return; }
        const matchKey = Object.keys(divisionMapForComp).find(k => { const v=divisionMapForComp[k]; const n = (typeof v==='string'?v:(v&&v.name? v.name:'')); return String(n||'').trim().toLowerCase()===name.toLowerCase(); });
        const action = matchKey ? 'update' : 'create';
        if (seenDivNames.has(name.toLowerCase())) { stages.divisions.push({ status:'error', row:r.__row, message:'Duplicate division in file', raw:r }); errors.push(`Divisions row ${r.__row}: duplicate name`); return; }
        seenDivNames.add(name.toLowerCase());
        stages.divisions.push({ status: action, row:r.__row, name, description:desc, matchKey });
      });

      // Teams
      const teams = parsed['Teams'] ? parsed['Teams'].rows : [];
      const seenTeamNumbers = new Set();
      teams.forEach(r => {
        const tnRaw = r.teamNumber;
        const teamNumber = (tnRaw!==undefined && tnRaw!==null && String(tnRaw)!=='') ? String(tnRaw).trim() : '';
        if (!teamNumber) { stages.teams.push({ status:'error', row:r.__row, message:'Missing teamNumber', raw:r }); errors.push(`Teams row ${r.__row}: missing teamNumber`); return; }
        if (seenTeamNumbers.has(teamNumber)) { stages.teams.push({ status:'error', row:r.__row, message:'Duplicate teamNumber in file', raw:r }); errors.push(`Teams row ${r.__row}: duplicate teamNumber`); return; }
        seenTeamNumbers.add(teamNumber);
        const teamName = String((r.teamName||'')).trim();
        const categoryName = String((r.categoryName||'')).trim();
        const divisionName = String((r.divisionName||'')).trim();
        const membersCsv = String((r.teamMembers||'')).trim();
        const memberEmails = membersCsv.length>0 ? membersCsv.split(',').map(s=>String(s||'').toLowerCase().trim()).filter(Boolean) : [];
        const unresolvedMembers = memberEmails.filter(e => !usersByEmail[e]);
        if (unresolvedMembers.length>0) { stages.teams.push({ status:'error', row:r.__row, message:`Unresolved user emails: ${unresolvedMembers.join(', ')}`, raw:r }); errors.push(`Teams row ${r.__row}: unresolved emails ${unresolvedMembers.join(', ')}`); return; }
        const matchTeam = Object.keys(existingTeams).find(k => String((existingTeams[k] && existingTeams[k].teamNumber)||'')===teamNumber);
        const action = matchTeam ? 'update' : 'create';
        stages.teams.push({ status: action, row:r.__row, teamNumber, teamName, categoryName, divisionName, memberEmails, matchKey: matchTeam });
      });

      // Events
      const events = parsed['Events'] ? parsed['Events'].rows : [];
      const seenEventNumbers = new Set();
      events.forEach(r => {
        const evNumRaw = r.eventNumber;
        const eventNumber = (evNumRaw!==undefined && evNumRaw!==null && String(evNumRaw)!=='') ? String(evNumRaw).trim() : '';
        if (!eventNumber) { stages.events.push({ status:'error', row:r.__row, message:'Missing eventNumber', raw:r }); errors.push(`Events row ${r.__row}: missing eventNumber`); return; }
        if (seenEventNumbers.has(eventNumber)) { stages.events.push({ status:'error', row:r.__row, message:'Duplicate eventNumber in file', raw:r }); errors.push(`Events row ${r.__row}: duplicate eventNumber`); return; }
        seenEventNumbers.add(eventNumber);
        const eventName = String((r.eventName||'')).trim();
        const fullTitle = String((r.fullTitle||'')).trim();
        const company = String((r.company||'')).trim();
        const isQuiz = String((r.isQuiz||'')).toLowerCase()==='true' || r.isQuiz===true;
        const excludeEvent = String((r.excludeEvent||'')).toLowerCase()==='true' || r.excludeEvent===true;
        const judgesCsv = String((r.judges||'')).trim();
        const judgeEmails = judgesCsv.length>0 ? judgesCsv.split(',').map(s=>String(s||'').toLowerCase().trim()).filter(Boolean) : [];
        const unresolvedJudges = judgeEmails.filter(e => !usersByEmail[e]);
        if (unresolvedJudges.length>0) { stages.events.push({ status:'error', row:r.__row, message:`Unresolved judge emails: ${unresolvedJudges.join(', ')}`, raw:r }); errors.push(`Events row ${r.__row}: unresolved judge emails ${unresolvedJudges.join(', ')}`); return; }
        const assocCsv = String((r.associatedTeams||'')).trim();
        const assocNums = assocCsv.length>0 ? assocCsv.split(',').map(s=>String(s||'').trim()).filter(Boolean) : [];
        // map assocNums to existing team keys (if any) by teamNumber
        const assocKeys = assocNums.map(num => { const k = Object.keys(existingTeams).find(kk => String((existingTeams[kk] && existingTeams[kk].teamNumber)||'')===num); return k || null; });
        const matchEvent = Object.keys(existingEvents).find(k => String((existingEvents[k] && (existingEvents[k].number || existingEvents[k].number))||'')===eventNumber);
        const action = matchEvent ? 'update' : 'create';
        stages.events.push({ status: action, row:r.__row, eventNumber, eventName, fullTitle, company, isQuiz, excludeEvent, judgeEmails, assocNums, assocKeys, matchKey: matchEvent });
      });

      // Event Penalties (simple staging)
      const pens = parsed['Event Penalties'] ? parsed['Event Penalties'].rows : [];
      pens.forEach(r => {
        const eventNumber = String((r.eventNumber||'')).trim();
        const penaltyName = String((r.penaltyName||'')).trim();
        const penaltyValue = r.penaltyValue;
        if (!eventNumber) { stages.penalties.push({ status:'error', row:r.__row, message:'Missing eventNumber', raw:r }); errors.push(`Event Penalties row ${r.__row}: missing eventNumber`); return; }
        if (!penaltyName) { stages.penalties.push({ status:'error', row:r.__row, message:'Missing penaltyName', raw:r }); errors.push(`Event Penalties row ${r.__row}: missing penaltyName`); return; }
        stages.penalties.push({ status:'create_or_update', row:r.__row, eventNumber, penaltyName, penaltyValue });
      });

      setStaged(stages);
      setStagedSummary({ users: stages.users.length, categories: stages.categories.length, divisions: stages.divisions.length, teams: stages.teams.length, events: stages.events.length, penalties: stages.penalties.length, errors: errors.length });
      setImportErrors(errors);
    } catch (err) {
      console.error('import parse failed', err);
      alert('Failed to parse file');
    } finally { setParsing(false); }
  };

  const handleFileChange = (e) => { const f = e.target.files && e.target.files[0]; handleFileInput(f); };

  const applyStaged = async () => {
    if (!competition) { alert('Please select a target competition'); return; }
    if (!staged || Object.keys(staged).length===0) { alert('Nothing staged to apply'); return; }
    if (importErrors && importErrors.length>0) { alert('There are errors in the staged import. Fix them before applying.'); return; }
    if (dryRun) { if (!window.confirm('Dry run is enabled. Do you want to switch off Dry Run and actually apply changes?')) return; }

    if (!window.confirm(`Apply staged changes to competition ${competition}? This will write data to the database.`)) return;
    setApplyInProgress(true);
    const createdKeys = { categories: {}, divisions: {}, teams: {}, events: {} };
    const existingTeams = (teamsMap && teamsMap[competition]) || {};
    const existingEvents = (eventsMap && eventsMap[competition]) || {};
    const categoryMapForComp = (globalSettings && globalSettings.categories && globalSettings.categories[competition]) || {};
    const divisionMapForComp = (globalSettings && globalSettings.divisions && globalSettings.divisions[competition]) || {};
    try {
      // 0) Users — create Firebase Auth accounts without sending invite emails
      const importedUsersByEmail = {}; // email -> { uid, ...userData } for users created this run
      for (const u of (staged.users||[])) {
        if (u.status !== 'create') continue;
        if (dryRun) continue;
        try {
          const tempPassword = 'Amc2024xx!';
          const firebaseUser = await createUserWithEmailAndPassword(secondaryAuth, u.email, tempPassword);
          const uid = firebaseUser.user.uid;
          await signOut(secondaryAuth);
          const userData = { email: u.email, username: u.username, firstName: u.firstName, lastName: u.lastName, role: u.role, defaultCompetition: u.defaultCompetition, uid };
          await firebase.insertUser(uid, userData);
          importedUsersByEmail[u.email] = { uid, ...userData };
        } catch (err) {
          console.error('user create failed', u.email, err);
        }
      }
      // 1) Categories
      for (const c of (staged.categories||[])) {
        if (c.status === 'create') {
          if (dryRun) continue;
          try {
            // store category as simple display string (app expects string values)
            const res = await firebase.insertCategory(c.name, competition);
            const key = res && res.key ? res.key : (res && res.name ? res.name : null);
            if (key) createdKeys.categories[c.name] = key;
          } catch (err) { console.error('cat create failed', err); }
        } else if (c.status === 'update') {
          if (dryRun) continue;
          try {
            if (c.matchKey) await firebase.patchCategory(c.matchKey, c.name);
          } catch (err) { console.error('cat patch failed', err); }
        }
      }
      // 2) Divisions
      for (const d of (staged.divisions||[])) {
        if (d.status === 'create') {
          if (dryRun) continue;
          try {
            const res = await firebase.insertDivision(d.name, competition);
            const key = res && res.key ? res.key : (res && res.name ? res.name : null);
            if (key) createdKeys.divisions[d.name] = key;
          } catch (err) { console.error('div create failed', err); }
        } else if (d.status === 'update') {
          if (dryRun) continue;
          try { if (d.matchKey) await firebase.patchDivision(d.matchKey, d.name); } catch(err){ console.error('div patch failed', err); }
        }
      }

      // Prepare maps for teams
      const teamPayloads = {};
      // 3) Teams
      for (const t of (staged.teams||[])) {
        // resolve category/division keys by name (existing or newly created)
        let categoryKey = null;
        if (t.categoryName) {
          categoryKey = Object.keys(categoryMapForComp || {}).find(k => { const v = categoryMapForComp[k]; const n = (typeof v === 'string' ? v : (v && v.name ? v.name : '')); return String(n||'').trim().toLowerCase() === String(t.categoryName).trim().toLowerCase(); });
          if (!categoryKey && createdKeys.categories && createdKeys.categories[t.categoryName]) categoryKey = createdKeys.categories[t.categoryName];
        }
        let divisionKey = null;
        if (t.divisionName) {
          divisionKey = Object.keys(divisionMapForComp || {}).find(k => { const v = divisionMapForComp[k]; const n = (typeof v === 'string' ? v : (v && v.name ? v.name : '')); return String(n||'').trim().toLowerCase() === String(t.divisionName).trim().toLowerCase(); });
          if (!divisionKey && createdKeys.divisions && createdKeys.divisions[t.divisionName]) divisionKey = createdKeys.divisions[t.divisionName];
        }

        const teamObj = { teamNumber: t.teamNumber, teamName: t.teamName, category: categoryKey || '', division: divisionKey || '' };

        // member UIDs resolved from emails — check context first, then users created this run
        const memberUids = (t.memberEmails||[]).map(email => {
          const e = String(email||'').toLowerCase().trim();
          const fromContext = Object.keys(usersMap||{}).find(k => (((usersMap[k]||{}).email||'').toLowerCase().trim() === e));
          if (fromContext) return fromContext;
          return importedUsersByEmail[e] ? importedUsersByEmail[e].uid : null;
        }).filter(Boolean);

        // build teamMembers object mapping uid -> user object
        const teamMembersObj = memberUids.reduce((acc, uid) => {
          if (teamUsersMap && teamUsersMap[uid]) acc[uid] = teamUsersMap[uid];
          else if (usersMap && usersMap[uid]) acc[uid] = usersMap[uid];
          else { const u = Object.values(importedUsersByEmail).find(x => x.uid === uid); if (u) acc[uid] = u; }
          return acc;
        }, {});
        teamObj.teamMembers = teamMembersObj;

        // save payload locally for later event association
        teamPayloads[t.teamNumber] = teamObj;

        if (t.status === 'create') {
          if (dryRun) continue;
          try {
            const res = await firebase.insertTeam(teamObj, competition);
            const key = res && res.key ? res.key : (res && res.name ? res.name : null);
            if (key) {
              createdKeys.teams[t.teamNumber] = key;
              // update user records to include this team under user.teamsNew[competition]
              await Promise.all(memberUids.map(async (uid) => {
                try {
                  const user = await firebase.getUser(uid) || {};
                  const existing = (user.teamsNew && user.teamsNew[competition]) || [];
                  user.teamsNew = { ...(user.teamsNew || {}), [competition]: Array.from(new Set([...existing, key])) };
                  await firebase.patchUser(uid, user, competition);
                } catch (err) {
                  console.error('failed to patch user teamsNew for', uid, err);
                }
              }));
            }
          } catch(err){ console.error('team create failed', err); }
        } else if (t.status === 'update') {
          if (dryRun) continue;
          try {
            if (t.matchKey) {
              // matchKey is team key
              await firebase.patchTeam(t.matchKey, teamObj, competition);
              // reconcile user membership: fetch existing team members from DB
              try {
                const existingTeam = await firebase.getTeam(t.matchKey, competition) || {};
                const prevMembers = existingTeam.teamMembers ? Object.keys(existingTeam.teamMembers) : [];
                const added = memberUids.filter(u => !prevMembers.includes(u));
                const removed = prevMembers.filter(u => !memberUids.includes(u));
                // add team to added users
                await Promise.all(added.map(async uid => {
                  const user = await firebase.getUser(uid) || {};
                  const existing = (user.teamsNew && user.teamsNew[competition]) || [];
                  user.teamsNew = { ...(user.teamsNew || {}), [competition]: Array.from(new Set([...existing, t.matchKey])) };
                  await firebase.patchUser(uid, user, competition);
                }));
                // remove team from removed users
                await Promise.all(removed.map(async uid => {
                  const user = await firebase.getUser(uid) || {};
                  const existing = (user.teamsNew && user.teamsNew[competition]) || [];
                  user.teamsNew = { ...(user.teamsNew || {}), [competition]: existing.filter(x => x !== t.matchKey) };
                  await firebase.patchUser(uid, user, competition);
                }));
              } catch (err) { console.error('failed to reconcile team members', err); }
            }
          } catch(err){ console.error('team patch failed', err); }
        }
      }

      // 4) Events
      // helper: add eventKey to team's associatedEvents array (if not present)
      const addEventToTeam = async (teamKey, eventKey, eventPayload) => {
        try {
          const team = await firebase.getTeam(teamKey, competition) || {};
          const existing = Array.isArray(team.associatedEvents) ? team.associatedEvents : (team.associatedEvents ? Object.values(team.associatedEvents) : []);
          // eventPayload optional — prefer it to avoid timing issues
          let eventData = eventPayload || null;
          if (!eventData) {
            try { eventData = await firebase.getEvent(eventKey, competition) || {}; } catch(e){ eventData = {}; }
          }
          const eventObj = { ...(firebase.Event ? firebase.Event(eventData) : eventData), uid: eventKey };
          // ensure number/name exist (coerce from eventData)
          if (!eventObj.number && eventData && eventData.number) eventObj.number = Number(eventData.number) || eventData.number;
          if (!eventObj.name && eventData && eventData.name) eventObj.name = eventData.name;
          const has = existing.some(ev => (ev && ev.uid) ? ev.uid === eventKey : ev === eventKey);
          if (!has) {
            const newArr = existing.concat([eventObj]);
            team.associatedEvents = newArr;
            await firebase.patchTeam(teamKey, team, competition);
            console.debug('addEventToTeam patched', teamKey, eventObj);
          }
        } catch (err) { console.error('addEventToTeam failed', teamKey, eventKey, err); }
      };
        const removeEventFromTeam = async (teamKey, eventKey) => {
          try {
            const team = await firebase.getTeam(teamKey, competition) || {};
            const existing = Array.isArray(team.associatedEvents) ? team.associatedEvents : (team.associatedEvents ? Object.values(team.associatedEvents) : []);
            const has = existing.some(ev => (ev && ev.uid) ? ev.uid === eventKey : ev === eventKey);
            if (has) {
              const newArr = existing.filter(ev => { if (ev && ev.uid) return ev.uid !== eventKey; return ev !== eventKey; });
              team.associatedEvents = newArr;
              await firebase.patchTeam(teamKey, team, competition);
            }
          } catch (err) { console.error('removeEventFromTeam failed', teamKey, eventKey, err); }
        };

        const normalizeJudgeRefs = (refs) => {
          const collected = new Set();
          const pushUid = (entry, key) => {
            if (!entry) return;
            if (typeof entry === 'string') { collected.add(entry); return; }
            if (typeof entry === 'object') {
              const uid = entry.uid || entry.user || entry.userUid || key || null;
              if (uid) collected.add(uid);
            }
          };
          if (Array.isArray(refs)) {
            refs.forEach(item => pushUid(item));
          } else if (refs && typeof refs === 'object') {
            Object.keys(refs).forEach(k => pushUid(refs[k], k));
          }
          return Array.from(collected);
        };

        const ensureUserEventAssignment = async (uid, eventKey, add) => {
          if (dryRun) return;
          if (!uid || !eventKey) return;
          try {
            const user = (await firebase.getUser(uid)) || {};
            const eventsRaw = (user.eventsNew && user.eventsNew[competition]) || [];
            const existing = Array.isArray(eventsRaw)
              ? [...eventsRaw]
              : (eventsRaw && typeof eventsRaw === 'object' ? Object.keys(eventsRaw) : []);
            const hasEvent = existing.includes(eventKey);
            if (add && hasEvent) return;
            if (!add && !hasEvent) return;
            const updated = add ? Array.from(new Set([...existing, eventKey])) : existing.filter(id => id !== eventKey);
            user.eventsNew = { ...(user.eventsNew || {}), [competition]: updated };
            await firebase.patchUser(uid, user, competition);
          } catch (err) {
            console.error('Failed to sync user eventsNew for', uid, eventKey, err);
          }
        };

        const syncJudgeEventAssignments = async (eventKey, newJudgeUids = [], prevJudgeUids = []) => {
          if (dryRun) return;
          try {
            const normalizedNew = Array.from(new Set((newJudgeUids || []).filter(Boolean)));
            const normalizedPrev = Array.from(new Set((prevJudgeUids || []).filter(Boolean)));
            const toRemove = normalizedPrev.filter(uid => !normalizedNew.includes(uid));
            await Promise.all([
              ...normalizedNew.map(uid => ensureUserEventAssignment(uid, eventKey, true)),
              ...toRemove.map(uid => ensureUserEventAssignment(uid, eventKey, false))
            ]);
          } catch (err) {
            console.error('Failed syncing judges for event', eventKey, err);
          }
        };

      for (const e of (staged.events||[])) {



        const evObj = { number: Number(e.eventNumber)||e.eventNumber, name: e.eventName||'', fullTitle: e.fullTitle||'', company: e.company||'', isQuiz: !!e.isQuiz, excludeEvent: !!e.excludeEvent };
         // resolve judges to UID->user object map
         const judgeUids = Array.from(new Set((e.judgeEmails||[]).map(email => {
           const em = String(email||'').toLowerCase().trim();
           return Object.keys(usersMap||{}).find(k => (((usersMap[k]||{}).email||'').toLowerCase().trim() === em));
         }).filter(Boolean)));
         const judgesObj = {};
         judgeUids.forEach(uid => { judgesObj[uid] = usersMap[uid] || {}; });
         evObj.judgesNew = judgesObj;


        // build associatedTeams array of full team objects { ...firebase.Team(teamData), uid }
        const assocObjs = (e.assocNums||[]).map(num => {
          const existingKey = Object.keys(existingTeams).find(k => String((existingTeams[k] && existingTeams[k].teamNumber)||'')===num);
          if (existingKey) return { ...(firebase.Team(existingTeams[existingKey])), uid: existingKey };
          if (createdKeys.teams && createdKeys.teams[num]) {
            const createdKey = createdKeys.teams[num];
            const payload = teamPayloads[num] || {};
            return { ...(firebase.Team(payload)), uid: createdKey };
          }
          return null;
        }).filter(Boolean);
        evObj.associatedTeams = assocObjs;

        if (e.status === 'create') {
          if (dryRun) continue;
          try {
            const res = await firebase.insertEvent(evObj, competition);
            const key = res && res.key ? res.key : (res && res.name ? res.name : null);
            if (key) {
              createdKeys.events[e.eventNumber] = key;
              // ensure each associated team's associatedEvents includes this event
              await Promise.all(assocObjs.map(async (t) => {
                if (t && t.uid) await addEventToTeam(t.uid, key, evObj);
              }));
              await syncJudgeEventAssignments(key, judgeUids, []);
            }
          } catch(err){ console.error('event create failed', err); }
        } else if (e.status === 'update') {
          if (dryRun) continue;
          try {
            if (e.matchKey) {
              // fetch previous associated teams (if available) to reconcile
              const prevEvent = existingEvents[e.matchKey] || await firebase.getEvent(e.matchKey, competition) || {};
              const prevJudgeUids = normalizeJudgeRefs(prevEvent ? (prevEvent.judgesNew || prevEvent.judges) : []);
              const prevTeams = Array.isArray(prevEvent.associatedTeams) ? prevEvent.associatedTeams.map(t=> t && t.uid ? t.uid : (typeof t === 'string' ? t : null)).filter(Boolean) : [];
              const newTeams = assocObjs.map(t => t && t.uid ? t.uid : null).filter(Boolean);

              // patch the event
              await firebase.patchEvent(e.matchKey, evObj, competition);

              // add event to newly associated teams
              const toAdd = newTeams.filter(x => !prevTeams.includes(x));
              await Promise.all(toAdd.map(async tk => await addEventToTeam(tk, e.matchKey, evObj)));

              // remove event from teams no longer associated
              const toRemove = prevTeams.filter(x => !newTeams.includes(x));
              await Promise.all(toRemove.map(async tk => await removeEventFromTeam(tk, e.matchKey)));
              await syncJudgeEventAssignments(e.matchKey, judgeUids, prevJudgeUids);
            }
          } catch(err){ console.error('event patch failed', err); }
        }
      }

      alert('Apply complete' + (dryRun ? ' (dry run - no writes performed)' : ''));
    } catch (err) {
      console.error('apply failed', err);
      alert('Apply failed - see console');
    } finally { setApplyInProgress(false); }
  };

  // simple helper to render staged lists
  const renderStagedList = (items, title) => (
    <Grid2 container direction="column" gap={1} sx={{ p:1 }}>
      <Typography variant="subtitle1">{title} ({items ? items.length : 0})</Typography>
      {(!items || items.length===0) ? <Typography variant="body2">(none)</Typography> : items.map((it, idx) => (
        <div key={idx} style={{ padding:6, borderBottom:'1px solid #eee' }}>
          <strong>{it.status}</strong>: {it.name||it.teamNumber||it.eventNumber||''} {it.message? ` — ${it.message}` : ''}
        </div>
      ))}
    </Grid2>
  );

  return (
    <Grid2 container direction="column" sx={{ p: { xs:2, sm:3 }, gap:2, mx:'auto', width:'100%' }}>
      <Tabs value={tabIndex} onChange={onTabChange}>
        <Tab label="Export" />
        <Tab label="Import" />
      </Tabs>

      {tabIndex===0 && (
        <div>
          <Typography variant="h4">Export</Typography>
          <Divider />

          <Grid2 container direction="column" gap={1}>
            <Typography variant="h6">Competition</Typography>
            <CompetitionDropdown value={competition} onChange={setCompetition} />
            <FormControlLabel control={<Checkbox checked={includeMeta} onChange={(e)=>setIncludeMeta(e.target.checked)} />} label={`Competition Metadata`} />
          </Grid2>

          <Divider />

          <Grid2 container direction="column" gap={1}>
            <Typography variant="h6">Teams</Typography>
            <FormControlLabel control={<Checkbox checked={teamsGroupAll} onChange={(e)=>setTeamsGroupAll(e.target.checked)} />} label={`All (${counts.teamsCount})`} />
            <FormControlLabel control={<Checkbox checked={teamsAll} onChange={(e)=>{ setTeamsAll(e.target.checked); setTeamsGroupAll(e.target.checked && teamsScores && teamMembers); }} />} label={`Teams (${counts.teamsCount})`} />
            <FormControlLabel control={<Checkbox checked={teamsScores} onChange={(e)=>{ setTeamsScores(e.target.checked); setTeamsGroupAll(teamsAll && e.target.checked && teamMembers); }} />} label={`Scores`} />
            <FormControlLabel control={<Checkbox checked={teamMembers} onChange={(e)=>{ setTeamMembers(e.target.checked); setTeamsGroupAll(teamsAll && teamsScores && e.target.checked); }} />} label={`Team Members (${counts.teamMembersCount})`} />
          </Grid2>

          <Divider />

          <Grid2 container direction="column" gap={1}>
            <Typography variant="h6">Events</Typography>
            <FormControlLabel control={<Checkbox checked={eventsGroupAll} onChange={(e)=>setEventsGroupAll(e.target.checked)} />} label={`All (${counts.eventsCount})`} />
            <FormControlLabel control={<Checkbox checked={eventsAll} onChange={(e)=>{ setEventsAll(e.target.checked); setEventsGroupAll(e.target.checked && eventsPenalties && eventsBase && assocTeams && judges); }} />} label={`Events (${counts.eventsCount})`} />
            <FormControlLabel control={<Checkbox checked={eventsPenalties} onChange={(e)=>{ setEventsPenalties(e.target.checked); setEventsGroupAll(eventsAll && e.target.checked && eventsBase && assocTeams && judges); }} />} label={`Penalties (${counts.penaltiesCount})`} />
            <FormControlLabel control={<Checkbox checked={eventsBase} onChange={(e)=>{ setEventsBase(e.target.checked); setEventsGroupAll(eventsAll && eventsPenalties && e.target.checked && assocTeams && judges); }} />} label={`Base Scoresheet`} />
            <FormControlLabel control={<Checkbox checked={assocTeams} onChange={(e)=>{ setAssocTeams(e.target.checked); setEventsGroupAll(eventsAll && eventsPenalties && eventsBase && e.target.checked && judges); }} />} label={`Associated Teams (${counts.assocCount})`} />
            <FormControlLabel control={<Checkbox checked={judges} onChange={(e)=>{ setJudges(e.target.checked); setEventsGroupAll(eventsAll && eventsPenalties && eventsBase && assocTeams && e.target.checked); }} />} label={`Judges (${counts.judgesCount})`} />
          </Grid2>

          <Divider />

          <Grid2 container direction="column" gap={1}>
            <Typography variant="h6">Users</Typography>
            <FormControlLabel control={<Checkbox checked={usersGroupAll} onChange={(e)=>setUsersGroupAll(e.target.checked)} />} label={`All (${counts.usersCount})`} />
            <FormControlLabel control={<Checkbox checked={users} onChange={(e)=>{ setUsers(e.target.checked); setUsersGroupAll(e.target.checked && adminUsers && liabilities); }} />} label={`Users (${counts.usersCount})`} />
            <FormControlLabel control={<Checkbox checked={adminUsers} onChange={(e)=>{ setAdminUsers(e.target.checked); setUsersGroupAll(users && e.target.checked && liabilities); }} />} label={`Admin Users (${counts.adminCount})`} />
            <FormControlLabel control={<Checkbox checked={liabilities} onChange={(e)=>{ setLiabilities(e.target.checked); setUsersGroupAll(users && adminUsers && e.target.checked); }} />} label={`Liability Signatories (${counts.liabilityCount})`} />
          </Grid2>

          <Divider />

          <Grid2 container direction="column" gap={1}>
            <Typography variant="h6">Categories & Divisions</Typography>
            <FormControlLabel control={<Checkbox checked={categories} onChange={(e)=>setCategories(e.target.checked)} />} label={`Categories (${categoriesCount})`} />
            <FormControlLabel control={<Checkbox checked={divisions} onChange={(e)=>setDivisions(e.target.checked)} />} label={`Divisions (${divisionsCount})`} />
          </Grid2>

          <Divider />

          <Grid2 container justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={prepareImportSelection} disabled={exporting} sx={{ mr:1 }}>
              Prepare Import XLSX
            </Button>
            <Button variant="contained" onClick={generateXlsx} disabled={exporting}>
              {exporting ? <><CircularProgress size={18} color="inherit" sx={{ mr:1 }} />Exporting...</> : 'Export XLSX'}
            </Button>
          </Grid2>
        </div>
      )}

      {tabIndex===1 && (
        <div>
          <Typography variant="h4">Import</Typography>
          <Divider />

          <Grid2 container direction="row" gap={2} alignItems="center">
            <CompetitionDropdown value={competition} onChange={setCompetition} />
            <Button variant="contained" component="label">Upload XLSX<input hidden accept=".xlsx" type="file" onChange={handleFileChange} /></Button>
            <Typography variant="body2">{importFileName}</Typography>
            <FormControlLabel control={<Checkbox checked={dryRun} onChange={(e)=>setDryRun(e.target.checked)} />} label={`Dry Run (no writes)`} />
            <Button variant="contained" color="primary" onClick={applyStaged} disabled={applyInProgress || parsing || (importErrors && importErrors.length>0)}>
              {applyInProgress ? 'Applying...' : 'Apply'}
            </Button>
          </Grid2>

          <Divider />

          <Grid2 container direction="row" gap={2}>
            <Grid2 xs={6}>
              <Typography variant="h6">Staged Summary</Typography>
              <div style={{ padding:8 }}>
                <div>Users: {(stagedSummary.users||0)}</div>
                <div>Categories: {(stagedSummary.categories||0)}</div>
                <div>Divisions: {(stagedSummary.divisions||0)}</div>
                <div>Teams: {(stagedSummary.teams||0)}</div>
                <div>Events: {(stagedSummary.events||0)}</div>
                <div>Penalties: {(stagedSummary.penalties||0)}</div>
                <div>Errors: {(stagedSummary.errors||0)}</div>
              </div>
              {(importErrors && importErrors.length>0) && (
                <div style={{ color:'red' }}>
                  <Typography variant="subtitle2">Errors</Typography>
                  {importErrors.map((err,i)=> <div key={i}>{err}</div>)}
                </div>
              )}
            </Grid2>
            <Grid2 xs={6}>
              <Typography variant="h6">Details</Typography>
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
                <Select value={entityView} onChange={(e)=>setEntityView(e.target.value)} size="small">
                  <MenuItem value={'users'}>Users</MenuItem>
                  <MenuItem value={'categories'}>Categories</MenuItem>
                  <MenuItem value={'divisions'}>Divisions</MenuItem>
                  <MenuItem value={'teams'}>Teams</MenuItem>
                  <MenuItem value={'events'}>Events</MenuItem>
                  <MenuItem value={'penalties'}>Penalties</MenuItem>
                </Select>
                <Select value={statusFilter} onChange={(e)=>setStatusFilter(e.target.value)} size="small">
                  <MenuItem value={'all'}>All</MenuItem>
                  <MenuItem value={'create'}>Create</MenuItem>
                  <MenuItem value={'update'}>Update</MenuItem>
                  <MenuItem value={'no-op'}>No-op</MenuItem>
                  <MenuItem value={'error'}>Errors</MenuItem>
                </Select>
                <Button size="small" onClick={downloadErrors} disabled={!(importErrors && importErrors.length>0)}>Download Errors</Button>
                <Button size="small" onClick={()=>{ if (!filteredItems || filteredItems.length===0) { alert('No items to apply'); return; } if (!window.confirm(`Apply ${entityView} staged items?`)) return; /* proceed with apply of all staged (current behavior) */ applyStaged(); }} variant="contained">Apply</Button>
              </div>
              <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Row</TableCell>
                      <TableCell>Key</TableCell>
                      <TableCell>Summary</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(filteredItems || []).map((it) => (
                      <TableRow key={`${entityView}-${it.__idx}`} hover>
                        <TableCell>{it.status}</TableCell>
                        <TableCell>{it.row || it.__row || ''}</TableCell>
                        <TableCell>{it.matchKey || it.matchKey || ''}</TableCell>
                        <TableCell>{(it.email || it.teamNumber || it.name || it.eventNumber || it.category || '')}</TableCell>
                        <TableCell>{it.message || ''}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={()=>openDetail(it)}>View / Edit</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Dialog open={detailOpen} onClose={closeDetail} fullWidth maxWidth="md">
                <DialogTitle>Staged Item</DialogTitle>
                <DialogContent>
                  <Typography variant="body2">Edit the staged item as JSON and click Save to update the staged import.</Typography>
                  <textarea style={{ width:'100%', height:260, fontFamily:'monospace', marginTop:8 }} value={detailData ? JSON.stringify(detailData, null, 2) : ''} onChange={(e)=> setDetailData(() => { try { return JSON.parse(e.target.value); } catch(_) { return detailData; } })} />
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeDetail}>Cancel</Button>
                  <Button onClick={()=>{ if (!detailData) return; updateStagedItem(entityView, detailData.__idx, detailData); closeDetail(); }} variant="contained">Save</Button>
                </DialogActions>
              </Dialog>
            </Grid2>
          </Grid2>
        </div>
      )}
    </Grid2>
  );
};

export default AdminExportPage;
